import { ListItem, UnderlyingYieldBearingAssetListItem } from '@/components';
import BarDial from '@/components/atoms/BarDial/BarDial';
import APP_CONFIG from '@/config/app';
import { UnderlyingYieldBearingAssetInterface } from '@/interfaces';
import {
  formatCompositionPercentage,
  formatNumberToAPYString,
} from '@/utils/formatUtils';
import { FC, useEffect, useState } from 'react';
interface YieldBearingAssetUnderlyingYieldBearingAssetAllocationListProps {
  underlyingYieldBearingAssets: UnderlyingYieldBearingAssetInterface[];
  className?: string;
  canChangeComposition?: boolean;
  onCompositionChange?: (
    newUnderlyingYieldBearingAssets: UnderlyingYieldBearingAssetInterface[],
  ) => void;
  onCompositionChangeDone?: (
    newUnderlyingYieldBearingAssets: UnderlyingYieldBearingAssetInterface[],
  ) => void;
}
const YieldBearingAssetUnderlyingYieldBearingAssetAllocationList: FC<
  YieldBearingAssetUnderlyingYieldBearingAssetAllocationListProps
> = ({
  underlyingYieldBearingAssets,
  className,
  canChangeComposition = false,
  onCompositionChange,
  onCompositionChangeDone,
}) => {
  const [
    revisedUnderlyingYieldBearingAssets,
    setRevisedUnderlyingYieldBearingAssets,
  ] = useState(underlyingYieldBearingAssets);
  const [initialCompositions, setInitialCompositions] = useState<number[]>([]);
  useEffect(() => {
    setRevisedUnderlyingYieldBearingAssets(underlyingYieldBearingAssets);
    setInitialCompositions(
      underlyingYieldBearingAssets.map((asset) => asset.composition || 0),
    );
  }, [underlyingYieldBearingAssets]);
  const calculateDeltaBasedCompositions = (index: number, newValue: number) => {
    if (index < 0 || index >= revisedUnderlyingYieldBearingAssets.length) {
      return revisedUnderlyingYieldBearingAssets;
    }
    const newComposition = newValue / 100;
    const effectiveInitialCompositions =
      initialCompositions.length === revisedUnderlyingYieldBearingAssets.length
        ? initialCompositions
        : revisedUnderlyingYieldBearingAssets.map(
            (asset) => asset.composition || 0,
          );
    const delta = newComposition - effectiveInitialCompositions[index];
    if (
      Math.abs(delta) < Number.EPSILON ||
      revisedUnderlyingYieldBearingAssets.length <= 1
    ) {
      return revisedUnderlyingYieldBearingAssets.map((asset, i) =>
        i === index ? { ...asset, composition: newComposition } : asset,
      );
    }
    const otherAssetsCount = revisedUnderlyingYieldBearingAssets.length - 1;
    const equalDeltaPerAsset = delta / otherAssetsCount;
    const adjustedAssets = revisedUnderlyingYieldBearingAssets.map(
      (asset, i) => {
        if (i === index) {
          return { ...asset, composition: newComposition };
        }
        const newCompositionForOther =
          effectiveInitialCompositions[i] - equalDeltaPerAsset;
        return {
          ...asset,
          composition: Math.max(0, Math.min(1, newCompositionForOther)),
        };
      },
    );
    let totalComposition = adjustedAssets.reduce(
      (sum, asset) => sum + (asset.composition || 0),
      0,
    );
    if (Math.abs(totalComposition - 1) > Number.EPSILON) {
      const diff = 1 - totalComposition;
      const otherAssets = adjustedAssets.filter((_, i) => i !== index);
      const totalOtherComposition = otherAssets.reduce(
        (sum, asset) => sum + (asset.composition || 0),
        0,
      );
      if (totalOtherComposition > 0) {
        otherAssets.forEach((asset) => {
          const proportion = (asset.composition || 0) / totalOtherComposition;
          asset.composition! += diff * proportion;
          asset.composition = Math.max(0, Math.min(1, asset.composition!));
        });
      } else {
        adjustedAssets[index].composition! += diff;
        adjustedAssets[index].composition = Math.max(
          0,
          Math.min(1, adjustedAssets[index].composition!),
        );
      }
      totalComposition = adjustedAssets.reduce(
        (sum, asset) => sum + (asset.composition || 0),
        0,
      );
    }
    return adjustedAssets.map((asset) => {
      let composition = isNaN(asset.composition!) ? 0 : asset.composition!;
      composition = Math.max(0, Math.min(1, composition));
      return { ...asset, composition };
    });
  };
  const handleSliderChange = (index: number) => (value: number) => {
    if (!onCompositionChange) return;
    const clampedValue = Math.max(0, Math.min(100, value));
    if (isNaN(clampedValue)) return;
    if (initialCompositions.length === 0) {
      setInitialCompositions(
        revisedUnderlyingYieldBearingAssets.map(
          (asset) => asset.composition || 0,
        ),
      );
    }
    const validatedAssets = calculateDeltaBasedCompositions(
      index,
      clampedValue,
    );
    setRevisedUnderlyingYieldBearingAssets(validatedAssets);
    onCompositionChange(validatedAssets);
  };
  const handleSliderChangeDone = (index: number) => (value: number) => {
    if (!onCompositionChangeDone) return;
    const clampedValue = Math.max(0, Math.min(100, value));
    if (isNaN(clampedValue)) return;
    const validatedAssets = calculateDeltaBasedCompositions(
      index,
      clampedValue,
    );
    setRevisedUnderlyingYieldBearingAssets(validatedAssets);
    onCompositionChangeDone(validatedAssets);
    setInitialCompositions([]);
  };
  return (
    <div className={`flex flex-col w-full gap-4 ${className}`}>
      {revisedUnderlyingYieldBearingAssets.map(
        (underlyingYieldBearingAsset, index) => {
          const color =
            APP_CONFIG.TOKEN_COLORS[index % APP_CONFIG.TOKEN_COLORS.length];
          const compositionPercentage = parseFloat(
            formatCompositionPercentage(
              underlyingYieldBearingAsset.composition || 0,
            ),
          );
          return (
            <div
              key={underlyingYieldBearingAsset.symbol || `asset-${index}`}
              className="flex items-start justify-between w-full gap-md"
            >
              <div
                className={`flex items-center gap-4 ${
                  canChangeComposition ? 'flex-1' : ''
                }`}
              >
                <UnderlyingYieldBearingAssetListItem
                  underlyingYieldBearingAsset={underlyingYieldBearingAsset}
                  {...(!canChangeComposition && { leftColor: color })}
                />
              </div>
              <div
                className={`flex items-center w-full gap-4 flex-1 ${
                  canChangeComposition ? '' : ''
                }`}
              >
                {canChangeComposition ? (
                  <div className="flex flex-row w-full">
                    <ListItem
                      mainText={`${formatCompositionPercentage(
                        underlyingYieldBearingAsset.composition || 0,
                      )}%`}
                      subText={formatNumberToAPYString(
                        underlyingYieldBearingAsset.apy || 0,
                      )}
                      className="flex-1"
                    />
                    <BarDial
                      value={compositionPercentage}
                      editable={true}
                      onChange={handleSliderChange(index)}
                      onChangeDone={handleSliderChangeDone(index)}
                      className="flex-1 m-auto"
                      fillColor={color}
                      fillColorOpacity="bg-opacity-80"
                      showBreakpoints
                      showPercentageLabels
                    />
                  </div>
                ) : (
                  <ListItem
                    mainText={`${formatCompositionPercentage(
                      underlyingYieldBearingAsset.composition || 0,
                    )}%`}
                    subText={formatNumberToAPYString(
                      underlyingYieldBearingAsset.apy || 0,
                    )}
                    align="right"
                    fullWidth={false}
                    className="ml-auto"
                  />
                )}
              </div>
            </div>
          );
        },
      )}
    </div>
  );
};
export default YieldBearingAssetUnderlyingYieldBearingAssetAllocationList;
